/**
 * The device layer: keyboard and mouse listeners, pointer lock, and the
 * per-frame edges the game actually asks about.
 *
 * `inputMap.js` does the translation and is pure. This is the part that has to
 * touch the browser, and it is kept thin for exactly that reason — everything
 * here is about the awkward realities of real devices:
 *
 *   - A key pressed and released inside one frame must still count. Polling a
 *     held-key set would miss it entirely, so a keydown also lands in a sticky
 *     set that survives until the next poll consumes it.
 *   - Mouse look is a *delta stream*, not a state. Movement events arrive
 *     between frames and several can land in one, so they accumulate and the
 *     poll drains them. Reading `movementX` at frame time instead would throw
 *     away most of a fast flick.
 *   - Losing pointer lock or window focus releases everything. Otherwise
 *     holding W and alt-tabbing away leaves the player walking into a wall with
 *     no keyup ever coming to stop them.
 *   - Pointer lock is the only way to get a mouse look that does not run out of
 *     screen. It can only be requested from a real user gesture, so the canvas
 *     asks for it on click and the game shows a "click to play" veil until it
 *     is granted.
 */

import { ACTIONS, DIRECTIONS, boundKeys, idleInput, resolve, validateBindings } from './inputMap.js';
import { createGamepad } from './gamepad.js';

export { ACTIONS };

/**
 * `gamepad` is the pad layer (`core/gamepad.js`), made here unless one is
 * handed in, and `look` is the player's settings for the camera: a speed
 * multiplier and an invert, read every frame so a slider moves the camera the
 * moment it moves.
 *
 * The pad does not need pointer lock and does not get it: a keyboard only
 * drives the game while the mouse is captured (that is what clicking in is),
 * and a pad drives it whenever nothing else owns the screen. `padLive` is that
 * second switch, and `main.js` holds it.
 */
export function createInput(
  bindings,
  { target = globalThis, element = null, gamepad = null, look: lookSettings = () => ({}) } = {}
) {
  validateBindings(bindings);
  const swallow = boundKeys(bindings);
  const baseSensitivity = bindings.mouse.sensitivity;
  const baseInvert = bindings.mouse.invertY ? -1 : 1;
  const pad = gamepad ?? createGamepad({ settings: lookSettings });
  /** Which device last did something: what every prompt names its keys for. */
  let device = 'keyboard';
  const deviceListeners = new Set();
  function use(next) {
    if (next === device) return;
    device = next;
    for (const listener of deviceListeners) listener(device);
  }
  /** A pad driving the game: nothing else owns the screen. */
  let padLive = true;
  /** Left stick click runs until the stick comes back to rest, as on any pad game. */
  let padSprint = false;
  /** Directions down last frame, for their edges (the fan steps on them). */
  let directionsHeld = new Set();
  /**
   * Pad verbs still held from before the pad was handed back to the world.
   *
   * A press that closes a panel (A on Accept, B on a grade sheet) is still
   * down on the first frame the world reads the pad again, and read as a fresh
   * press there it did a second thing: Accept closed the board and the same A
   * opened it again. A verb held at the moment the pad goes live counts only
   * once it has been let go and pressed again.
   */
  let padLatch = new Set();
  let wasLive = true;

  /** Physically down right now. */
  const down = new Set();
  /** Pressed since the last poll, whether or not it is still down. */
  const tapped = new Set();
  const buttonsDown = new Set();
  const buttonsTapped = new Set();

  /** Accumulated since the last poll, in radians once scaled. */
  let lookX = 0;
  let lookY = 0;
  let look = { x: 0, y: 0 };

  let move = { x: 0, y: 0 };
  let held = new Set();
  let pressed = new Set();
  let released = new Set();
  let locked = false;
  const lockListeners = new Set();

  /**
   * Whether the key belongs to a field rather than to the game.
   *
   * Bound keys are swallowed, and almost every letter in this game is bound —
   * so the moment anything asked the player to type, half the alphabet went to
   * the character controller instead of into the box. The hiring screen's name
   * field could not be given a D, an E, a W or an A, which is most of a name.
   *
   * The DOM already knows which one has focus, so ask it rather than adding a
   * mode for the input layer to be in and remember to leave.
   */
  function typingInto(node) {
    if (!node) return false;
    if (node.isContentEditable) return true;
    return /^(input|textarea|select)$/i.test(node.tagName ?? '');
  }

  function onKeyDown(event) {
    // Auto-repeat is not a new press; treating it as one would fire interact
    // thirty times a second while a key rests.
    if (event.repeat) return;
    if (typingInto(event.target)) return;
    if (!swallow.has(event.code)) return;
    // Space scrolls the page and the arrows scroll it too. Only bound keys are
    // swallowed, so the browser's own shortcuts still work.
    event.preventDefault();
    down.add(event.code);
    tapped.add(event.code);
    use('keyboard');
  }

  function onKeyUp(event) {
    if (typingInto(event.target)) return;
    if (!swallow.has(event.code)) return;
    event.preventDefault();
    down.delete(event.code);
  }

  function onMouseDown(event) {
    if (!locked) return;
    event.preventDefault();
    buttonsDown.add(event.button);
    buttonsTapped.add(event.button);
    use('keyboard');
  }

  function onMouseUp(event) {
    buttonsDown.delete(event.button);
  }

  function onMouseMove(event) {
    if (!locked) return;
    const chosen = lookSettings() ?? {};
    const sensitivity = baseSensitivity * (chosen.sensitivity ?? 1);
    const invert = baseInvert * (chosen.invertY ? -1 : 1);
    const dx = event.movementX ?? 0;
    const dy = event.movementY ?? 0;
    lookX += dx * sensitivity;
    lookY += dy * sensitivity * invert;
    if (Math.abs(dx) + Math.abs(dy) > 2) use('keyboard');
  }

  function onContextMenu(event) {
    // Right mouse is a game button here, so the OS menu would eat it.
    if (locked) event.preventDefault();
  }

  function onBlur() {
    down.clear();
    buttonsDown.clear();
  }

  function onLockChange() {
    locked = document.pointerLockElement === element;
    if (!locked) onBlur();
    for (const listener of lockListeners) listener(locked);
  }

  target.addEventListener?.('keydown', onKeyDown);
  target.addEventListener?.('keyup', onKeyUp);
  target.addEventListener?.('blur', onBlur);
  target.addEventListener?.('mousedown', onMouseDown);
  target.addEventListener?.('mouseup', onMouseUp);
  target.addEventListener?.('mousemove', onMouseMove);
  target.addEventListener?.('contextmenu', onContextMenu);
  globalThis.document?.addEventListener?.('pointerlockchange', onLockChange);

  return {
    /** `{ x, y }`, +y forward and +x right. Never longer than 1. */
    get move() {
      return move;
    },
    /** Mouse delta since the last poll, in radians. Drained every frame. */
    get look() {
      return look;
    },
    get locked() {
      return locked;
    },
    /** 'keyboard' or 'gamepad': whichever was used last. */
    get device() {
      return device;
    },
    onDeviceChange(listener) {
      deviceListeners.add(listener);
      return () => deviceListeners.delete(listener);
    },
    /** The pad layer, for its menu edges, its triggers and its rumble. */
    pad,
    /** The pad's reading this frame (`resolvePad`'s shape). */
    get padState() {
      return pad.state;
    },
    get padLive() {
      return padLive;
    },
    set padLive(value) {
      padLive = Boolean(value);
    },
    /** Whether the left stick click has the body running. */
    get padSprinting() {
      return padSprint;
    },

    held: (action) => held.has(action),
    /** True on the single frame the verb went down. */
    pressed: (action) => pressed.has(action),
    /** True on the single frame the verb came back up. */
    released: (action) => released.has(action),

    /** Asks for pointer lock. Must be called from inside a user gesture. */
    requestLock() {
      element?.requestPointerLock?.();
    },
    releaseLock() {
      globalThis.document?.exitPointerLock?.();
    },
    onLockChange(listener) {
      lockListeners.add(listener);
      return () => lockListeners.delete(listener);
    },

    /**
     * Polls the devices and recomputes the edges. Call once per frame, before
     * anything that reads the input — every `pressed()` answer is fixed at this
     * moment, so two consumers in the same frame cannot disagree about it.
     */
    update() {
      const keys = new Set([...down, ...tapped]);
      const buttons = new Set([...buttonsDown, ...buttonsTapped]);
      tapped.clear();
      buttonsTapped.clear();

      const next = locked ? resolve(bindings, keys, buttons) : idleInput();
      const actions = new Set(next.actions);
      let moveNow = next.move;
      let lookNow = { x: lookX, y: lookY };

      /*
       * The pad, merged underneath. Its verbs join the keyboard's, its stick
       * is used when it is pushed further than the keys are, and its look is
       * added to the mouse's, so a player can put the pad down and pick the
       * mouse up mid-stride and nothing is dropped.
       */
      const state = pad.poll();
      if (state.active) use('gamepad');
      if (padLive && !wasLive) {
        padLatch = new Set(state.held);
        if (state.gas > 0.5) padLatch.add('snap');
        for (const name of ['up', 'down', 'left', 'right']) if (state.dpad[name]) padLatch.add(`dpad-${name}`);
      }
      for (const name of [...padLatch]) {
        const still = name.startsWith('dpad-') ? state.dpad[name.slice(5)] : name === 'snap' ? state.gas > 0.5 || state.held.has(name) : state.held.has(name);
        if (!still) padLatch.delete(name);
      }
      wasLive = padLive;
      const latched = (name) => padLatch.has(name);
      if (padLive && pad.connected) {
        if (pad.pressedVerbs().includes('sprint')) padSprint = true;
        if (Math.hypot(state.move.x, state.move.y) < 0.1) padSprint = false;
        for (const verb of state.held) if (ACTIONS.includes(verb) && verb !== 'sprint' && !latched(verb)) actions.add(verb);
        if (padSprint) actions.add('sprint');
        if (Math.hypot(state.move.x, state.move.y) > Math.hypot(moveNow.x, moveNow.y)) moveNow = state.move;
        lookNow = { x: lookNow.x + state.look.x, y: lookNow.y + state.look.y };
        // Out in the world the d-pad's left and right are the time scale, and
        // up is the route line. On foot the right trigger snaps what you carry
        // to the plan; in a cart it is the gas, and nothing on foot is asked.
        if (state.dpad.left && !latched('dpad-left')) actions.add('timeDown');
        if (state.dpad.right && !latched('dpad-right')) actions.add('timeUp');
        if (state.dpad.up && !latched('dpad-up')) actions.add('route');
        if (state.dpad.down && !latched('dpad-down')) actions.add('help');
        if (state.gas > 0.5 && !latched('snap')) actions.add('snap');
      } else {
        padSprint = false;
      }

      // Directions as edges too, for the one thing that steps on them: the fan.
      const directions = new Set();
      if (locked) {
        for (const name of DIRECTIONS) if (bindings.keyboard[name].some((code) => keys.has(code))) directions.add(name);
      }
      if (pad.connected && state.dpad.up && !latched('dpad-up')) directions.add('forward');
      if (pad.connected && state.dpad.down && !latched('dpad-down')) directions.add('back');

      const previous = held;
      held = actions;
      pressed = new Set([...actions].filter((name) => !previous.has(name)));
      for (const name of directions) if (!directionsHeld.has(name)) pressed.add(name);
      directionsHeld = directions;
      released = new Set([...previous].filter((name) => !held.has(name)));
      move = moveNow;

      look = lookNow;
      lookX = 0;
      lookY = 0;
    },

    /** Drops everything held. Used when the game takes control away. */
    clear() {
      down.clear();
      tapped.clear();
      buttonsDown.clear();
      buttonsTapped.clear();
      held = new Set();
      pressed = new Set();
      released = new Set();
      move = { x: 0, y: 0 };
      look = { x: 0, y: 0 };
      lookX = 0;
      lookY = 0;
    },

    dispose() {
      target.removeEventListener?.('keydown', onKeyDown);
      target.removeEventListener?.('keyup', onKeyUp);
      target.removeEventListener?.('blur', onBlur);
      target.removeEventListener?.('mousedown', onMouseDown);
      target.removeEventListener?.('mouseup', onMouseUp);
      target.removeEventListener?.('mousemove', onMouseMove);
      target.removeEventListener?.('contextmenu', onContextMenu);
      globalThis.document?.removeEventListener?.('pointerlockchange', onLockChange);
    },
  };
}
